/**
 * Collection filter URL controller. Filter input change → debounced
 * URL build → Turbo-frame swap, plus the count + active-filters labels
 * that update once the swap finishes.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-07-14 - Added sort_by handling (handleSortChange)
 * Updated: 2026-09-26 - Sort reads a sort_by radio instead of a <select>
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { pjaxUrl } from './router.js';
import { fireChange, COLLECTION_SELECTOR } from './dom.js';

const COUNT_SELECTOR = '.js-collection-count';
const GRID_CONTENT_SELECTOR = '#collection-grid-content';
const FILTERS_SECTION_SELECTOR = '.js-collection-filters';
const ACTIVE_FILTERS_LABEL_SELECTOR = '.js-active-filters-label';
const SORT_ITEM_SELECTOR = '.js-sort-item';
const SORT_VALUE_SELECTOR = '.js-sort-value';
const DENIM_CHECKED_FILTERS_SELECTOR =
  '.fit-filters input[type="checkbox"]:checked, ' +
  '.color-filters input[type="checkbox"]:checked, ' +
  '.fabric-filters input[type="checkbox"]:checked';
const DENIM_GROUPS_SELECTOR = '.fit-filters, .color-filters, .fabric-filters';
const AVAILABILITY_PARAM = 'filter.v.availability';
const SORT_PARAM = 'sort_by';
const FILTER_DEBOUNCE_MS = 150;

// One in-flight filter swap at a time. activeFilterTeardown returns to
// the pending state on every new debounce schedule + on page:change.
let filterChangeTimer = null;
let activeFilterTeardown = null;

// Always read URL via window.location.search first — app.get('url.currentObj.search')
// is stale until pjax/turbo finishes. The denim checkbox handler unchecks
// siblings WITHOUT dispatching change: a synthetic change would race the
// native one (sibling-removal timer overwriting the addition timer).

function getCurrentSearch() {
  const fromLocation = window.location.search || '';
  if (fromLocation) return fromLocation.replace(/^\?+/, '');
  const fromApp = appGet('url.currentObj.search', '') || '';
  return fromApp.replace(/^\?+/, '');
}

function getCurrentPath() {
  return appGet('url.path', window.location.pathname).replace(/\?+$/, '');
}

function buildUpdatedSearch({ urlParams, name, value, isCheckbox, isChecked, valueInUrl, fieldset }) {
  const shouldRemoveFilter = isCheckbox ? !isChecked && valueInUrl : valueInUrl;

  if (shouldRemoveFilter) {
    if (value !== '') {
      const allValues = urlParams.getAll(name);
      urlParams.delete(name);
      for (const existing of allValues) {
        if (existing !== value) urlParams.append(name, existing);
      }
    } else {
      urlParams.delete(name);
    }
    let hasOtherFilters = false;
    for (const [key, val] of urlParams.entries()) {
      if (key !== AVAILABILITY_PARAM && val !== '') {
        hasOtherFilters = true;
        break;
      }
    }
    if (!hasOtherFilters) urlParams.delete(AVAILABILITY_PARAM);
    return urlParams.toString();
  }

  // Adding a filter. Denim groups (fit/color/fabric) are radio-like:
  // remove sibling group values from the URL before appending the new one.
  if (fieldset && isCheckbox && isChecked) {
    const siblingValues = [];
    fieldset.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input.name === name && input.value !== value) siblingValues.push(input.value);
    });
    const allValues = urlParams.getAll(name);
    urlParams.delete(name);
    for (const existing of allValues) {
      if (siblingValues.indexOf(existing) === -1) urlParams.append(name, existing);
    }
  }

  urlParams.append(name, value);
  if (name !== AVAILABILITY_PARAM) urlParams.set(AVAILABILITY_PARAM, '1');
  return urlParams.toString();
}

export function updateCountLabel() {
  const collection = document.querySelector(COLLECTION_SELECTOR);
  if (!collection) return;
  const countLabel = collection.querySelector(COUNT_SELECTOR);
  if (!countLabel) return;
  const cardSelector = appGet('dom.product.cardSelector', '.js-product-card:not(.grid-tout)');
  const count = collection.querySelectorAll(cardSelector).length;
  const stringKey = count !== 1 ? 'count_plural' : 'count_singular';
  countLabel.textContent = count + ' ' + appGet('i18n.collection.' + stringKey, '');
}

export function updateActiveFiltersLabel() {
  const filtersSection = document.querySelector(FILTERS_SECTION_SELECTOR);
  if (!filtersSection) return;
  // The label span is always server-rendered by collection-filters.liquid (no
  // markup is built in JS); this only updates its text and visibility.
  const label = filtersSection.querySelector(ACTIVE_FILTERS_LABEL_SELECTOR);
  if (!label) return;
  const titles = [];
  filtersSection.querySelectorAll(DENIM_CHECKED_FILTERS_SELECTOR).forEach((input) => {
    const radioField = input.closest('.radio-field');
    const title = radioField && radioField.querySelector('.filter-title');
    if (title) titles.push(title.textContent.trim());
  });
  const labelText = titles.join(', ');

  if (!labelText) {
    label.textContent = '';
    label.style.display = 'none';
    return;
  }

  label.textContent = labelText;
  // Explicit 'inline' (not '') — the span's base SCSS rule is `display: none`
  // (_collection-filters.scss), so clearing the inline style would leave it
  // hidden. See the "display: none defaults vs jQuery .show()" note in CLAUDE.md.
  label.style.display = 'inline';
}

// Mirrors the chosen sort's name into the toggle's value span (the same
// "static label + conditional value" pattern as updateActiveFiltersLabel),
// reading the option's own label text so it never needs a duplicate copy of
// the sort names. Hidden again when the collection's default order is picked.
export function updateSortValueLabel(input) {
  const sortItem = input.closest(SORT_ITEM_SELECTOR);
  if (!sortItem) return;
  const valueLabel = sortItem.querySelector(SORT_VALUE_SELECTOR);
  if (!valueLabel) return;

  const isDefault = input.value === sortItem.dataset.defaultSortBy;
  if (isDefault) {
    valueLabel.textContent = '';
    valueLabel.style.display = 'none';
    return;
  }

  const optionLabel = sortItem.querySelector(`label[for="${input.id}"]`);
  valueLabel.textContent = optionLabel ? optionLabel.textContent.trim() : '';
  // Explicit 'inline' — same display:none default + explicit-value gotcha as
  // updateActiveFiltersLabel above.
  valueLabel.style.display = 'inline';
}

// Wire turbo:frame-load + turbo:frame-missing on the target frame and kick
// off the navigation. Returns a teardown closure that detaches both
// listeners — caller stores it so the next change can clean up before
// installing fresh handlers.
function navigateToFilteredUrl(newUrl, container) {
  const frame = document.querySelector(container || GRID_CONTENT_SELECTOR);
  let frameLoadHandler = null;
  let frameMissingHandler = null;

  const teardown = () => {
    if (frame && frameLoadHandler) frame.removeEventListener('turbo:frame-load', frameLoadHandler);
    if (frame && frameMissingHandler) frame.removeEventListener('turbo:frame-missing', frameMissingHandler);
    frameLoadHandler = null;
    frameMissingHandler = null;
  };

  if (frame) {
    frameLoadHandler = () => {
      // Turbo frames don't move window.location; replaceState makes the
      // URL match the applied filters (so reload, share, back work).
      history.replaceState(history.state, document.title, newUrl);
      log('[Filter] frame-load → reasserted ' + newUrl);
      updateCountLabel();
      updateActiveFiltersLabel();
      teardown();
    };
    frameMissingHandler = () => {
      log('[Filter] frame-missing → full reload to ' + newUrl);
      teardown();
      window.location.href = newUrl;
    };
    frame.addEventListener('turbo:frame-load', frameLoadHandler);
    frame.addEventListener('turbo:frame-missing', frameMissingHandler);
  }

  pjaxUrl(newUrl, container, true);
  return teardown;
}

// Shared by every URL-mutating change handler (filters, sort): the frame to
// swap is always read from the same data-container attribute on the
// enclosing filters section.
function containerFor(filtersSection) {
  return filtersSection ? filtersSection.dataset.container : null;
}

function buildUrl(params) {
  return getCurrentPath() + (params.length > 0 ? '?' + params : '');
}

// Shared by every URL-mutating change handler (filters, sort): debounce,
// tear down the prior in-flight swap, then kick off the new one. Keeps a
// single in-flight frame swap invariant regardless of which control fired.
function scheduleNavigation(newUrl, container) {
  clearTimeout(filterChangeTimer);
  if (activeFilterTeardown) activeFilterTeardown();
  filterChangeTimer = setTimeout(() => {
    log('[Filter] debounce fired → ' + newUrl);
    activeFilterTeardown = navigateToFilteredUrl(newUrl, container);
  }, FILTER_DEBOUNCE_MS);
}

export function handleCollectionFiltersChange(input) {
  const name = input.name;
  const value = input.value;
  const isCheckbox = input.type === 'checkbox';
  const isChecked = input.checked;
  const urlParams = new URLSearchParams(getCurrentSearch());
  const valueInUrl = urlParams.getAll(name).indexOf(value) !== -1;

  if (isCheckbox) {
    if (isChecked && valueInUrl) return;
    if (!isChecked && !valueInUrl) return;
  }

  const filtersSection = input.closest(FILTERS_SECTION_SELECTOR);
  const fieldset = filtersSection ? input.closest(DENIM_GROUPS_SELECTOR) : null;
  const container = containerFor(filtersSection);

  const params = buildUpdatedSearch({
    urlParams,
    name,
    value,
    isCheckbox,
    isChecked,
    valueInUrl,
    fieldset,
  });
  const newUrl = buildUrl(params);

  log(
    '[Filter] change → newUrl=' + newUrl + ' (name=' + name +
    ', value=' + value + ', checked=' + isChecked + ')'
  );

  scheduleNavigation(newUrl, container);
}

// Sort is a single-value replace (URLSearchParams.set), not the toggle
// add/remove semantics filters use — the sort_by radio group always has
// exactly one value, so there's nothing to add/remove relative to the URL.
export function handleSortChange(input) {
  const value = input.value;
  updateSortValueLabel(input);
  const urlParams = new URLSearchParams(getCurrentSearch());
  // "Featured" is the collection's own default order, so it clears sort_by
  // rather than pinning the default into the URL.
  const sortItem = input.closest(SORT_ITEM_SELECTOR);
  if (sortItem && value === sortItem.dataset.defaultSortBy) {
    urlParams.delete(SORT_PARAM);
  } else {
    urlParams.set(SORT_PARAM, value);
  }

  const filtersSection = input.closest(FILTERS_SECTION_SELECTOR);
  const container = containerFor(filtersSection);
  const params = urlParams.toString();
  const newUrl = buildUrl(params);

  log('[Filter] sort change → newUrl=' + newUrl + ' (value=' + value + ')');

  scheduleNavigation(newUrl, container);
}

export function handleCollectionFilterToggleClick(input) {
  // Native checkboxes already toggle; the change handler manages the URL.
  // Re-clicking a selected radio doesn't fire change, so we simulate
  // "toggle off" by unchecking and dispatching change manually.
  if (input.type === 'checkbox') return;
  const currentSearch = window.location.search || appGet('url.currentObj.search', '') || '';
  const shouldRemove = input.checked && currentSearch.indexOf(input.value) !== -1;
  if (!shouldRemove) return;
  input.checked = false;
  fireChange(input);
}

export function handleDenimFilterCheckboxClick(input) {
  if (!input.checked) return;
  const fieldset = input.closest('fieldset');
  if (!fieldset) return;
  fieldset.querySelectorAll('input[type="checkbox"]:checked').forEach((other) => {
    if (other !== input) other.checked = false;
  });
}

export function handleRemoveSelectFilter(button) {
  const filterItem = button.closest('.filter-item');
  const select = filterItem && filterItem.querySelector('select');
  if (!select) {
    log('[Filter] remove-select-filter: no select in .filter-item');
    return;
  }
  select.value = '';
  fireChange(select);
}

export function teardownFilters() {
  clearTimeout(filterChangeTimer);
  filterChangeTimer = null;
  if (activeFilterTeardown) {
    activeFilterTeardown();
    activeFilterTeardown = null;
  }
}

log('[Filter] controller registered');
